<script>
    import { onDestroy } from 'svelte';
    import { _, locale, waitLocale } from 'svelte-i18n';
    import { get } from 'svelte/store';
    import {
        storePacks, getStorePacks, getConfig, user,
        playerStateLoaded, playerGrowthFund, playerMonsterGrowthFund, endExpiredSession, isSessionExpired,
    } from './stores';
    import { canOfferPack, ownedOneTimePerk } from './lib/perk-ownership.js';
    import Loader from './lib/Loader.svelte';
    import {setUserProperties} from "firebase/analytics";
    import {analytics} from "./notifications.js";
    import {buy_pack_route, game_name} from "./constants.js";
    import {basketDescription} from "./lib/basket-description.js";

    let getPacks;
    let loading = $state(true);
    let error = $state(null);
    let _user;
    let initError = $state(false);
    let checkoutPage;
    let processing = $state(false);

    let queryParams = $derived(new URLSearchParams(window.location.search));
    let packId = $derived(queryParams.get('packId'));
    let packTitle = $derived(queryParams.get('packTitle') || '');

    // Released on destroy: a 403 clears the session and swaps this page for <Login/>, and a
    // subscription left behind restarted the purchase when the player logged in again.
    const unsubscribeUser = user.subscribe((data) => {
        if (data === null) return;

        setUserProperties(analytics, {
            username: data.user.username,
            id: data.user.id,
        });

        _user = data;
        getPacks = getStorePacks(true).then(startPurchase);
        getConfig();
    });
    onDestroy(unsubscribeUser);

    const generatePaymentIntent = async (storePack) => {
        try {
            // The game's mail deep link carries no title, so the pack names itself, which needs
            // the language file. A link with a title (Save More) does not wait on it.
            const linkTitle = decodeURIComponent(packTitle);
            if (!linkTitle.trim()) await waitLocale();
            let params;
            params = {
                packId: storePack.internalId,
                currencyCode: storePack.paymentHub.price.currency,
                price: storePack.paymentHub.price.amount,
                paymentsProcessor: 'PaymentHub',
                email: $user.user.email,
                description: basketDescription({
                    packTitle: linkTitle,
                    pack: storePack,
                    side: $user.user.side,
                    translate: (key) => $_(key, { default: '' }),
                    fallback: game_name,
                }),
                locale: $locale
            };

            const response = await fetch(buy_pack_route, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(params),
            });

            // web-shop's 403: the session is gone (see Checkout.svelte). Clearing it shows <Login/>
            // on this page instead of reporting it as critical (#1349).
            if (isSessionExpired(response)) {
                endExpiredSession(buy_pack_route);
                return;
            }

            // 503: the server cannot sell this pack right now (see Checkout.svelte). Expected, so
            // info; the shop then shows what is still on offer.
            if (response.status === 503) {
                Rollbar.info('Payment intent refused: pack not available right now');
                window.location.href = '/supplies';
                return;
            }

            if (!response.ok) {
                initError = true;
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const res = await response.json();

            if (res.checkoutPage) {
                checkoutPage = res.checkoutPage;
            }

            setTimeout(() => {
                if (res.checkoutPage) {
                    window.history.replaceState({}, document.title, window.location.pathname);
                    window.open(res.checkoutPage, '_self');
                    processing = true;
                } else {
                    processing = false;
                    window.location.href = '/supplies';
                }
            }, 50);
        } catch (error) {
            Rollbar.critical('Error creating payment intent: ' + error)
            window.location.href = '/supplies';
            throw error; // or handle error as you need
        }
    }

    // Runs once get-packs (and the non-fatal player-state / static-data fetches) have settled,
    // so the ownership flags below are the ones that came back with this load rather than
    // their defaults. This deep link is the only purchase path with no section-level guard.
    function startPurchase() {
        const data = get(storePacks);
        // Nothing to buy from (get-packs failed, or PaymentHub priced none of the packs): the
        // shop shows the error or what is still on offer, instead of this spinner forever.
        if (data.length === 0) {
            window.location.href = '/supplies';
            return;
        }
        try {
            const foundPack = data.find(p => p.internalId.toString() === packId);
            if (!foundPack) {
                error = "Pack not found";
                window.location.href = '/supplies';
                return;
            }

            // A pack carrying a one-time perk (growthFund / monsterGrowthFund) must not be sold to a
            // player who already holds it: the backend records the perk with a plain insert, so the
            // payment can never validate while the player has already been charged. Also refused
            // while player state is unknown, since every flag then merely sits at its default.
            const playerState = {
                playerGrowthFund: get(playerGrowthFund),
                playerMonsterGrowthFund: get(playerMonsterGrowthFund),
            };
            if (!canOfferPack(foundPack, playerState, get(playerStateLoaded))) {
                const reason = ownedOneTimePerk(foundPack, playerState)
                    ? 'perk already owned'
                    : 'player state unavailable';
                Rollbar.info(`Refused /buyPack for pack ${foundPack.internalId}: ${reason}`);
                window.location.href = '/supplies/advancementRewards';
                return;
            }

            generatePaymentIntent(foundPack);
        } catch (err) {
            error = "Error loading pack: " + err.message;
            window.location.href = '/supplies';
        } finally {
            loading = false;
        }
    }
</script>
<div class="loading">
    <Loader />
</div>

<style>
    .loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 80vh;
    }
</style>